import { supabase } from '../../lib/supabase';
import { attemptReviewKey, getAttemptReview } from '../../shared/results/attempt-review-api';
import type {
  ResultBlock,
  ResultReviewItem,
  ResultWritingTask,
} from '../../shared/results/result-types';
import type { ResultSpeaking } from '../../shared/results/ResultDetail';

interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export interface ResultBands {
  listening_band: number | null;
  reading_band: number | null;
  writing_band: number | null;
  speaking_band: number | null;
  overall_band: number | null;
  published_at: string | null;
  /** Phase 175 — see shared/results/ResultDetail. Absent before that migration. */
  listening_raw?: number | null;
  listening_total?: number | null;
  reading_raw?: number | null;
  reading_total?: number | null;
  sections?: string[];
  kind?: string;
  is_drill?: boolean;
  /** Phase 188 — which test this was, and when it was handed in. */
  test_title?: string | null;
  exam_type?: string | null;
  submitted_at?: string | null;
}

/** Phase 188 — one shape for the review row, shared with the teacher's copy of
 *  the screen and with the read-only paper review. */
export type ReviewItem = ResultReviewItem;

export interface ItemCriterion {
  criterion: string;
  band: number;
  feedback: string;
}

/** AI-graded feedback for one new-type item (Read Aloud, Summarize, …). */
export interface ItemFeedback {
  section_kind: string;
  prompt: string;
  type_code: string | null;
  score: number;
  points_awarded: number;
  max_points: number;
  criteria: ItemCriterion[];
  feedback: string;
  transcript: string | null;
}

export interface RubricComment {
  quote?: string;
  text: string;
}

export interface WritingComments {
  task_index: number | null;
  comments: RubricComment[];
}

export interface ResultComments {
  writing: WritingComments[];
  speaking: RubricComment[];
}

export interface StudentResult {
  result: ResultBands | null;
  review: ReviewItem[];
  /** Phase 188 — the shared stimulus behind the review's block blanks. Absent
   *  on a payload from before that migration. */
  blocks?: ResultBlock[];
  items: ItemFeedback[];
  comments: ResultComments;
  /** Phase 175 — the student's own essay with the examiner's criteria, and
   *  Speaking parts with recording availability. Absent before that migration. */
  writing?: ResultWritingTask[];
  speaking?: ResultSpeaking | null;
}

export const studentResultKey = attemptReviewKey;

/** Phase 188 — the fetch moved to `shared/results` so the read-only paper
 *  review (which lives in the exam feature, and may not import this one) reads
 *  the same payload through the same key. Kept exported here because the two
 *  student screens and their tests address it by this name. */
export const getStudentResult = getAttemptReview;

/** One row of a student's attempt history (from student_view_results). */
export interface StudentResultRow {
  attempt_id: string;
  test_id: string;
  test_title: string;
  exam_type: 'academic' | 'general';
  kind: 'scheduled' | 'practice' | 'assignment';
  /** Phase 161: this attempt was a question-type drill, not a mock. Absent on
   *  payloads from before that migration — read it as `=== true`, never
   *  `!== false`, so a stale RPC keeps today's behaviour. */
  is_drill?: boolean;
  session_id: string | null;
  started_at: string;
  submitted_at: string | null;
  status: 'grading' | 'completed' | 'submitted';
  listening_band: number | null;
  reading_band: number | null;
  writing_band: number | null;
  speaking_band: number | null;
  overall_band: number | null;
  published_at: string | null;
  /** Phase 175 — enough about a drill to show it: its skill, its type, and
   *  how many of its graded questions were right. Absent before that
   *  migration. */
  drill_skill?: string | null;
  drill_type_code?: string | null;
  graded_count?: number | null;
  correct_count?: number | null;
  assignment_id?: string | null;
  /** Section kinds the test holds, in display order (Phase 175). */
  sections?: string[];
}

export const studentResultsKey = ['student', 'results'] as const;

/** Every non-in-progress attempt by the signed-in student, newest first. */
export async function getStudentResults(): Promise<StudentResultRow[]> {
  const { data, error } = await rpcUnknown.rpc('student_view_results');
  if (error !== null) throw new Error(error.message);
  return (data as StudentResultRow[] | null) ?? [];
}
